// 家用面板 P1b 卡片（docs/panel 05-ui-ux）：V-07 這台能做什麼、V-08 常用地點、V-09 新地點核准 / 跨城市告知。
// 只有主人看得到（API 只給主人）。網頁不需要先解鎖手機（V-09 的解鎖只在行動端）。
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ApiError } from '../../lib/api';
import {
  approvePanelLocation, capLines, deletePanelNetwork, exitPanelSafeMode, getPanelCaps, getPanelNetworks, networkSummary,
  rediagnosePanel, rememberPanelCity, requestPanelReport, unlockPanelDegrade,
  type LocationMode, type PanelCapsView, type PanelNetworksView,
} from './panels';

const MARK = { ok: '✔', no: '✖', partial: '△' } as const;
const HOURS = [1, 4, 8, 24];

function fmtTime(sec?: number): string {
  if (!sec) return '';
  const d = new Date(sec * 1000);
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

/** V-07 這台能做什麼 */
export function PanelCapsCard({ panelId }: { panelId: string }) {
  const { t } = useTranslation();
  const [v, setV] = useState<PanelCapsView | null>(null);
  const [detail, setDetail] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => {
    getPanelCaps(panelId).then(setV).catch(() => setV({ caps: null }));
  }, [panelId]);
  useEffect(load, [load]);

  const run = async (fn: () => Promise<unknown>, ok: string) => {
    setBusy(true);
    setMsg(null);
    try {
      await fn();
      setMsg(t(ok));
      load();
    } catch (e) {
      setMsg(e instanceof ApiError && e.code === 'DEGRADE_LOCKED' ? t('panels.cap.locked') : t('panels.errGeneric'));
    } finally {
      setBusy(false);
    }
  };

  const deg = v?.degrade;
  return (
    <div className="card" data-testid="panel-caps">
      <h2 style={{ marginTop: 0 }}>{t('panels.cap.title')}</h2>
      <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
        {capLines(v?.caps ?? null, !!v?.safe_mode).map((l) => (
          <li key={l.key} data-mark={l.mark}>
            <span aria-label={t(`panels.cap.mark_${l.mark}`)} style={{ display: 'inline-block', width: '1.6em' }}>{MARK[l.mark]}</span>
            {t(l.key)}
          </li>
        ))}
      </ul>
      {deg && deg.level > 0 && (
        <p className="error-text" data-testid="panel-degraded">{t(deg.level >= 2 ? 'panels.cap.degraded2' : 'panels.cap.degraded1')}</p>
      )}
      {deg?.locked && <p className="error-text">{t('panels.cap.locked')}</p>}
      <div className="dv-pairing-actions" style={{ flexWrap: 'wrap' }}>
        {v?.safe_mode && (
          <button className="btn-primary" disabled={busy} onClick={() => void run(() => exitPanelSafeMode(panelId), 'panels.cap.safeModeExitSent')}>
            {t('panels.cap.exitSafeMode')}
          </button>
        )}
        {deg && deg.level > 0 && !deg.locked && (
          <button className="btn-primary" disabled={busy} onClick={() => void run(() => rediagnosePanel(panelId, true), 'panels.cap.restoreSent')}>
            {t('panels.cap.restore')}
          </button>
        )}
        {deg?.locked && (
          <button className="btn-ghost" disabled={busy} onClick={() => void run(() => unlockPanelDegrade(panelId), 'panels.cap.unlocked')}>
            {t('panels.cap.unlock')}
          </button>
        )}
        <button className="btn-ghost" disabled={busy} onClick={() => void run(() => rediagnosePanel(panelId, false), 'panels.cap.rediagnoseSent')}>
          {t('panels.cap.rediagnose')}
        </button>
        <button className="btn-ghost" disabled={busy} onClick={() => void run(() => requestPanelReport(panelId), 'panels.cap.reportSent')}>
          {t('panels.cap.collectReport')}
        </button>
        <button className="btn-ghost" onClick={() => setDetail((x) => !x)}>{t('panels.cap.detail')}</button>
      </div>
      {msg && <p className="text-secondary" role="status">{msg}</p>}
      {detail && v?.caps && (
        <pre data-testid="panel-caps-raw" style={{ fontSize: 'var(--font-size-sm)', overflowX: 'auto' }}>{JSON.stringify(v.caps, null, 2)}</pre>
      )}
    </div>
  );
}

/** V-08 常用地點 + V-09 新地點核准 / 跨城市告知 */
export function PanelNetworksCard({ panelId, onRemoved }: { panelId: string; onRemoved: () => void }) {
  const { t } = useTranslation();
  const [v, setV] = useState<PanelNetworksView | null>(null);
  const [hours, setHours] = useState(4);
  const [askRing, setAskRing] = useState<null | { mode: LocationMode; hours?: number }>(null);
  const [confirmRemove, setConfirmRemove] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => {
    getPanelNetworks(panelId).then(setV).catch(() => setV({ networks: [] }));
  }, [panelId]);
  useEffect(load, [load]);

  const act = async (fn: () => Promise<unknown>, after?: () => void) => {
    setBusy(true);
    setMsg(null);
    try {
      await fn();
      after?.();
      load();
    } catch {
      setMsg(t('panels.errGeneric'));
    } finally {
      setBusy(false);
      setAskRing(null);
    }
  };

  const p = v?.pending;
  return (
    <>
      {p && (
        <div className="card" data-testid="panel-pending" style={{ borderColor: 'var(--color-warning, orange)' }}>
          <h2 style={{ marginTop: 0 }}>{t('panels.loc.pendingTitle')}</h2>
          <p>{t('panels.loc.pendingBody', { from: p.from_org || '—', to: p.org || t('panels.loc.unknownOrg'), city: p.city || '—' })}</p>
          {!askRing && !confirmRemove && (
            <div className="dv-pairing-actions" style={{ flexWrap: 'wrap' }}>
              {p.can_once && (
                <button className="btn-primary" disabled={busy} onClick={() => setAskRing({ mode: 'once' })}>{t('panels.loc.allowOnce')}</button>
              )}
              <label>
                <select value={hours} onChange={(e) => setHours(Number(e.target.value))} aria-label={t('panels.loc.hoursLabel')}>
                  {HOURS.map((h) => (
                    <option key={h} value={h}>{t(h === 4 ? 'panels.loc.hoursSuggested' : 'panels.loc.hoursN', { n: h })}</option>
                  ))}
                </select>
              </label>
              {p.can_once && (
                <button className="btn-primary" disabled={busy} onClick={() => setAskRing({ mode: 'hours', hours })}>{t('panels.loc.allowHours', { n: hours })}</button>
              )}
              <button className="btn-danger" disabled={busy} onClick={() => setConfirmRemove(true)}>{t('panels.loc.notHomeRemove')}</button>
              {p.can_once && (
                <button className="btn-link" disabled={busy} onClick={() => void act(() => approvePanelLocation(panelId, 'permanent'))}>
                  {t('panels.loc.makeHome')}
                </button>
              )}
            </div>
          )}
          {askRing && (
            <div data-testid="panel-ask-ring">
              <p>{t('panels.loc.ringAsk')}</p>
              <div className="dv-pairing-actions">
                <button className="btn-primary" disabled={busy} onClick={() => void act(() => approvePanelLocation(panelId, askRing.mode, askRing.hours, true))}>
                  {t('panels.loc.ringYes')}
                </button>
                <button className="btn-ghost" disabled={busy} onClick={() => void act(() => approvePanelLocation(panelId, askRing.mode, askRing.hours, false))}>
                  {t('panels.loc.ringNo')}
                </button>
              </div>
            </div>
          )}
          {confirmRemove && (
            <div>
              <p>{t('panels.removeConfirm')}</p>
              <div className="dv-pairing-actions">
                <button className="btn-danger" disabled={busy} onClick={() => void act(() => approvePanelLocation(panelId, 'remove'), onRemoved)}>
                  {t('panels.removeYes')}
                </button>
                <button className="btn-ghost" disabled={busy} onClick={() => setConfirmRemove(false)}>{t('common.cancel')}</button>
              </div>
            </div>
          )}
        </div>
      )}
      {v?.city_notice && (
        <div className="card" data-testid="panel-city">
          <p>{t('panels.loc.cityMoved', { from: v.city_notice.from || '—', to: v.city_notice.city })}</p>
          <div className="dv-pairing-actions">
            <button className="btn-primary" disabled={busy} onClick={() => void act(() => rememberPanelCity(panelId))}>{t('panels.loc.rememberCity')}</button>
            <button className="btn-danger" disabled={busy} onClick={() => setConfirmRemove(true)}>{t('panels.loc.notHomeRemove')}</button>
          </div>
        </div>
      )}
      <div className="card" data-testid="panel-networks">
        <h2 style={{ marginTop: 0 }}>{t('panels.net.title')}</h2>
        <p className="text-secondary">{t('panels.net.hint')}</p>
        {(v?.networks ?? []).length === 0 && <p className="text-secondary">{t('panels.net.empty')}</p>}
        <ul style={{ listStyle: 'none', padding: 0 }}>
          {(v?.networks ?? []).map((n) => {
            const s = networkSummary(n);
            return (
              <li key={n.id} style={{ display: 'flex', gap: 'var(--spacing-4)', alignItems: 'center' }}>
                <span>
                  {t(s.kindKey)}：{s.parts.join('・')}
                  {n.has_wifi ? `・${t('panels.net.wifi')}` : ''}
                  {s.until ? `・${t('panels.net.until', { time: fmtTime(s.until) })}` : ''}
                </span>
                <button className="btn-ghost" disabled={busy} onClick={() => void act(() => deletePanelNetwork(panelId, n.id))}>
                  {t('panels.net.delete')}
                </button>
              </li>
            );
          })}
        </ul>
        {msg && <p className="error-text">{msg}</p>}
      </div>
    </>
  );
}
